import React from 'react';
import { Popover, Box, Typography, IconButton, List, ListItem, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

interface TripInfo {
  tripID: string | number;
  tmsObjectNumber: string;
}

interface TraceabilityData {
  licensePlate: string;
  platform?: string;
  tripsInfo?: TripInfo[];
}

interface TraceabilityPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  data: TraceabilityData;
  useCustomPopover?: boolean;
}

const TraceabilityPopover: React.FC<TraceabilityPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  data,
  useCustomPopover = false
}) => {
  const { t } = useTranslation();

  const PopoverComponent = useCustomPopover ? Popover : Popover;

  const formatMessage = (message: string, licensePlate: string, platform?: string) => {
    const cleanMessage = platform ? message : message.replace(' sulla piattaforma', '');
    return cleanMessage.split(licensePlate).map((part, index, array) => (
      <React.Fragment key={index}>
        {part}
        {index < array.length - 1 && (
          <Typography component="span" sx={{ fontWeight: 'bold' }}>
            {licensePlate}
          </Typography>
        )}
      </React.Fragment>
    ));
  };

  return (
    <PopoverComponent
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: {
          padding: '0',
          maxWidth: '300px',
          border: '1px solid #CF0A2C',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          backgroundColor: '#FFFFFF',
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ color: '#CF0A2C', fontSize: '16px', fontWeight: 'bold' }}>
            {t('screens.fleetAndRoute.errorDialog.noteTitle')}
          </Typography>
          <IconButton 
            onClick={onClose}
            size="small"
            sx={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              color: '#333333'
            }}
          >
            <CloseIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </Box>
        {data?.tripsInfo && data.tripsInfo.length > 0 ? (
          <Box>
            <Typography sx={{ color: '#333333', fontSize: '14px', mb: 1 }}>
              {t('common.sidebars.addVehicle.untrackablePlateWithTrips', { 
                licensePlate: data.licensePlate
              }).split(data.licensePlate).map((part, index, array) => (
                <React.Fragment key={index}>
                  {part}
                  {index < array.length - 1 && (
                    <Typography component="span" sx={{ fontWeight: 'bold' }}>
                      {data.licensePlate}
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </Typography>
            <List sx={{ 
              listStyleType: 'disc', 
              pl: 2,
              '& .MuiListItem-root': {
                display: 'list-item',
                padding: '4px 0'
              }
            }}>
              {data.tripsInfo.map((trip) => (
                <ListItem key={trip.tripID} disablePadding>
                  <Button
                    onClick={() => window.open(`/tripdetails/${trip.tripID}`, '_blank')}
                    sx={{
                      color: '#CF0A2C',
                      textDecoration: 'underline',
                      padding: '0 4px',
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'none'
                      }
                    }}
                  >
                    {trip.tmsObjectNumber}
                  </Button>
                </ListItem>
              ))}
            </List>
            <Typography sx={{ color: '#333333', fontSize: '14px' }}>
              {data.platform ? 
                t('common.sidebars.addVehicle.isReportedAsUntrackable', { 
                  platform: data.platform 
                }) :
                'verificata come non tracciabile'
              }
            </Typography>
          </Box>
        ) : (
          <Typography>
            {formatMessage(
              t('common.sidebars.addVehicle.untrackablePlate', { 
                licensePlate: data.licensePlate,
                platform: data.platform || ''
              }),
              data.licensePlate,
              data.platform
            )}
          </Typography>
        )}
      </Box>
    </PopoverComponent>
  );
};

export default TraceabilityPopover; 